import { useState } from "react";
import { Flag } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
  { key: "inappropriate", label: "Not uplifting / inappropriate" },
  { key: "spam", label: "Spam or self-promotion" },
  { key: "harmful", label: "Harmful or dangerous" },
  { key: "gossip", label: "Reads like gossip or slander" },
  { key: "other", label: "Something else (admin will review)" },
];

/**
 * Inline report button used inside post / comment / testimony / memory menus.
 * Renders as a tiny "Flag" trigger; clicking opens a reason picker. Submitting
 * files /api/reports and shows a thankful toast.
 */
export default function ReportButton({ targetType, targetId, className = "", label = "Report" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async (reason) => {
    setBusy(true);
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason });
      toast.success("Thank you — an admin will review this.");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not file report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className={`relative inline-block ${className}`} data-testid={`report-${targetType}-${targetId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs text-[var(--jj-muted)] hover:text-burgundy transition-colors"
        title="Report"
        data-testid={`report-trigger-${targetType}-${targetId}`}
      >
        <Flag className="h-3.5 w-3.5" strokeWidth={1.6} />
        {label && <span>{label}</span>}
      </button>
      {open && (
        <div
          className="absolute z-20 right-0 mt-1 w-64 jj-card !p-2 bg-white shadow-lg"
          onMouseLeave={() => setOpen(false)}
          data-testid={`report-menu-${targetType}-${targetId}`}
        >
          <div className="text-[11px] uppercase tracking-wider text-burgundy px-2 pt-1 pb-1.5">Tell us what's wrong</div>
          <ul className="space-y-0.5">
            {REASONS.map((r) => (
              <li key={r.key}>
                <button
                  type="button"
                  onClick={() => send(r.key)}
                  disabled={busy}
                  className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-cream-surface text-jjblue disabled:opacity-50"
                  data-testid={`report-reason-${r.key}`}
                >
                  {r.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  );
}
